import { useState, useEffect } from 'react';
import { FileDown, MapPin, Loader2, QrCode, Calendar, AlertCircle } from 'lucide-react';
import { fetchPosterVenues, downloadVenuePoster } from '../../../api/poster';
import type { PosterVenue } from '../../../api/poster';

const DAYS_OPTIONS = [
  { value: 30, label: 'Next 30 days' },
  { value: 60, label: 'Next 60 days' },
  { value: 90, label: 'Next 90 days' },
  { value: 180, label: 'Next 6 months' },
];

export default function PosterGenerator() {
  const [venues, setVenues] = useState<PosterVenue[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [venuesError, setVenuesError] = useState<string | null>(null);

  const [selectedVenueKey, setSelectedVenueKey] = useState('');
  const [daysAhead, setDaysAhead] = useState(90);
  const [customQrUrl, setCustomQrUrl] = useState('');

  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [lastGenerated, setLastGenerated] = useState<string | null>(null);

  useEffect(() => {
    fetchPosterVenues()
      .then((data) => {
        setVenues(data);
        setVenuesLoading(false);
      })
      .catch((err: unknown) => {
        setVenuesError(err instanceof Error ? err.message : 'Failed to load venues');
        setVenuesLoading(false);
      });
  }, []);

  const selectedVenue = venues.find((v) => v.venueKey === selectedVenueKey);

  async function handleGenerate() {
    if (!selectedVenueKey) return;
    setGenerating(true);
    setGenerateError(null);
    try {
      await downloadVenuePoster(selectedVenueKey, {
        daysAhead,
        bookingUrl: customQrUrl.trim() || undefined,
      });
      setLastGenerated(selectedVenue?.venueName ?? selectedVenueKey);
    } catch (err: unknown) {
      setGenerateError(err instanceof Error ? err.message : 'Failed to generate poster');
    } finally {
      setGenerating(false);
    }
  }

  // Group venues by state for the <select> optgroups
  const venuesByState = venues.reduce<Record<string, PosterVenue[]>>((acc, v) => {
    const key = v.state ?? 'Other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(v);
    return acc;
  }, {});
  const states = Object.keys(venuesByState).sort();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-bark-900 dark:text-stone-100">Workshop Poster Generator</h2>
        <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
          Generate a print-ready PDF poster for any venue with upcoming workshop dates and a booking QR code.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Config panel */}
        <div className="lg:col-span-2 bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-6 space-y-5">

          {/* Venue selector */}
          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1.5">
              <MapPin size={13} className="inline mr-1 text-moss-600" />
              Venue
            </label>
            {venuesLoading ? (
              <div className="flex items-center gap-2 text-sm text-stone-400">
                <Loader2 size={14} className="animate-spin" /> Loading venues…
              </div>
            ) : venuesError ? (
              <div className="flex items-center gap-2 text-sm text-red-500">
                <AlertCircle size={14} /> {venuesError}
              </div>
            ) : (
              <select
                value={selectedVenueKey}
                onChange={(e) => setSelectedVenueKey(e.target.value)}
                className="w-full rounded-md border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-moss-500"
              >
                <option value="">— Select a venue —</option>
                {states.map((state) => (
                  <optgroup key={state} label={state}>
                    {venuesByState[state].map((v) => (
                      <option key={v.venueKey} value={v.venueKey}>
                        {v.venueName}{v.city ? ` · ${v.city}` : ''}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}
          </div>

          {/* Date window */}
          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1.5">
              <Calendar size={13} className="inline mr-1 text-moss-600" />
              Workshop window
            </label>
            <div className="flex flex-wrap gap-2">
              {DAYS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDaysAhead(opt.value)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                    daysAhead === opt.value
                      ? 'bg-moss-600 text-white'
                      : 'bg-stone-100 dark:bg-[#242d28] text-stone-600 dark:text-stone-300 hover:bg-stone-200 dark:hover:bg-[#2d3830]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom QR URL (optional) */}
          <div>
            <label className="block text-sm font-medium text-bark-900 dark:text-stone-100 mb-1.5">
              <QrCode size={13} className="inline mr-1 text-moss-600" />
              QR code URL{' '}
              <span className="font-normal text-stone-400">(optional — defaults to bonsaibar.com)</span>
            </label>
            <input
              type="url"
              value={customQrUrl}
              onChange={(e) => setCustomQrUrl(e.target.value)}
              placeholder="https://bonsaibar.com/workshops?venue=…"
              className="w-full rounded-md border border-stone-300 dark:border-stone-600 bg-white dark:bg-[#242d28] text-bark-900 dark:text-stone-100 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-moss-500 placeholder:text-stone-400"
            />
          </div>

          {/* Error */}
          {generateError && (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
              <AlertCircle size={14} /> {generateError}
            </div>
          )}

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={!selectedVenueKey || generating}
            className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-moss-600 hover:bg-moss-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition"
          >
            {generating ? (
              <>
                <Loader2 size={15} className="animate-spin" /> Generating…
              </>
            ) : (
              <>
                <FileDown size={15} /> Generate & Download Poster
              </>
            )}
          </button>

          {lastGenerated && !generating && !generateError && (
            <p className="text-xs text-moss-600 dark:text-moss-400">
              ✓ Poster for <strong>{lastGenerated}</strong> downloaded successfully.
            </p>
          )}
        </div>

        {/* Info panel */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-5">
            <h3 className="text-sm font-semibold text-bark-900 dark:text-stone-100 mb-3">What&apos;s on the poster</h3>
            <ul className="space-y-2.5 text-sm text-stone-600 dark:text-stone-400">
              <li className="flex items-start gap-2">
                <span className="text-moss-600 mt-0.5">▪</span>
                <span><strong className="text-bark-900 dark:text-stone-200">Bonsai Bar branding</strong> — company name and venue details</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-moss-600 mt-0.5">▪</span>
                <span><strong className="text-bark-900 dark:text-stone-200">Upcoming workshops</strong> — date, time, workshop name, spots remaining, and price per person</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-moss-600 mt-0.5">▪</span>
                <span><strong className="text-bark-900 dark:text-stone-200">Booking QR code</strong> — scannable code linking directly to the booking page</span>
              </li>
            </ul>
          </div>

          <div className="bg-stone-50 dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-5">
            <h3 className="text-sm font-semibold text-bark-900 dark:text-stone-100 mb-2">Print tips</h3>
            <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
              The poster is formatted as a US Letter PDF (8.5&quot; × 11&quot;). For best results, print at 100% scale with no margins on card stock. The PDF is vector-based — scale up to 11&quot; × 17&quot; without quality loss.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
